/* eslint-disable @typescript-eslint/no-use-before-define */
/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCustomRoomContext } from './room/CustomRoomContext'
import { addSecondsToDate, calculateTimeRemaining } from './LiveKit.utils'
import {
  TrackReference,
  TrackReferenceOrPlaceholder,
  useDataChannel,
  useEnsureParticipant,
  useEnsureRoom,
  useLocalParticipant,
  useParticipantInfo,
  useParticipants,
  useRemoteParticipants,
  useRoomContext,
  useRoomInfo,
} from '@livekit/components-react'
import {
  CustomTrack,
  HostResponse,
  LiveKitBid,
  LiveKitEndAllowUnmute,
  LiveKitEndRoom,
  LiveKitHandRaised,
  UserType,
} from './livekitTypes'
import { ConnectionState, DataPublishOptions, LocalParticipant, RemoteParticipant, Track } from 'livekit-client'
import { useCustomGridContext } from './room/CustomGridContext'
import { UserLayoutPosition, getGridAspect, calculateBoxPositionsForEachUser } from './liveKitHelpers'
import { useCustomLiveShoppingContext } from './room/LiveShopping/CustomLiveShoppingContext'

/**
 * A custom hook to determine if the local participant is a host, co-host, or both.
 *
 * @return {object} An object containing boolean values indicating whether the local participant is a host, co-host, or both.
 */

export const useGetIsLocalParticipantHostOrCohostHooks = () => {
  const { localParticipant } = useLocalParticipant()

  const [isLocalParticipantHost, setIsLocalParticipantHost] = useState(false)
  const [isLocalParticipantCohost, setIsLocalParticipantCohost] = useState(false)
  const { setIsLocalHost } = useCustomRoomContext()

  useEffect(() => {
    if (localParticipant && localParticipant.metadata) {
      const userType = JSON.parse(localParticipant.metadata).userType
      setIsLocalParticipantHost(userType === UserType.HOST)
      setIsLocalParticipantCohost(userType === UserType.CO_HOST)
      setIsLocalHost(userType === UserType.HOST)
    }
  }, [localParticipant])

  return {
    isLocalParticipantHost,
    isLocalParticipantCohost,
    isLocalParticipantHostOrCohost: isLocalParticipantHost || isLocalParticipantCohost,
  }
}

/**
 * A custom hook to retrieve the host and co-host participants in a room.
 *
 * @return {object} An object containing the host and co-host participant IDs and identities.
 */

export const useGetHostAndCoHost = () => {
  const [hostAndCoHosts, setHostAndCoHosts] = useState<string[]>([])
  const [hostAndCohostIdentities, setHostAndCohostIdentities] = useState<string[]>([])
  const participants = useParticipants()
  const [host, setHost] = useState<string[]>([])
  const [coHost, setCoHost] = useState<string[]>([])

  useEffect(() => {
    const hosts = participants.filter((p) => {
      if (p.metadata) {
        try {
          const metadata = JSON.parse(p.metadata)
          return metadata.userType === UserType.HOST
        } catch (e) {
          return false
        }
      }
      return false
    })
    const coHosts = participants.filter((p) => {
      if (p.metadata) {
        try {
          const metadata = JSON.parse(p.metadata)
          return metadata.userType === UserType.CO_HOST
        } catch (e) {
          return false
        }
      }
      return false
    })
    const hostCoHosts = participants.filter((p) => {
      if (p.metadata) {
        try {
          const metadata = JSON.parse(p.metadata)
          return metadata.userType === UserType.HOST || metadata.userType === UserType.CO_HOST
        } catch (e) {
          return false
        }
      }
      return false
    })

    const sids = hostCoHosts.map((p) => p.sid).sort()
    const identities = hostCoHosts.map((p) => p.identity).sort()
    if (sids.join(',') !== hostAndCoHosts.join(',')) {
      setHostAndCoHosts(sids)
    }

    const hostSid = hosts.map((p) => p.sid).sort()
    if (hostSid.join(',') !== host.join(',')) {
      setHost(hostSid)
    }

    const coHostSid = coHosts.map((p) => p.sid).sort()
    if (coHostSid.join(',') !== coHost.join(',')) {
      setCoHost(coHostSid)
    }

    if (identities.join(',') !== hostAndCohostIdentities.join(',')) {
      setHostAndCohostIdentities(identities)
    }
  }, [participants, hostAndCoHosts, hostAndCohostIdentities, host, coHost])

  return {
    hostAndCoHosts,
    hostAndCohostIdentities,
    host,
    coHost,
  }
}

/**
 * A custom hook to determine if a track is pinned in the current grid layout.
 *
 * @param {TrackReferenceOrPlaceholder} trackReference - The track reference to check for pinned status.
 * @return {boolean} True if the track is pinned, false otherwise.
 */

export const useIsPinnedTrackHook = (trackReference: TrackReferenceOrPlaceholder) => {
  const { pinnedParticipants } = useCustomGridContext()
  const sidSource = trackReference.publication?.trackSid ?? trackReference.participant.sid

  return pinnedParticipants.includes(sidSource)
}

/**
 * A custom hook to categorize and filter participant tracks based on their roles and states.
 *
 * @param {CustomTrack[]} tracks - An array of CustomTrack objects representing participant tracks.
 * @return {object} An object containing categorized tracks, participant counts, and other relevant data.
 */

// For Audio meeting
export const useCustomStagedParticipantHooks = (tracks: CustomTrack[]) => {
  const { stagedParticipants, handRaisedParticipants } = useCustomRoomContext()
  const [stagedTracks, setStagedTracks] = useState<CustomTrack[]>([])
  const [otherTracks, setOtherTracks] = useState<CustomTrack[]>([])
  const [moderatorTracks, setModeratorTracks] = useState<CustomTrack[]>([])
  const [handRaisedTracks, setHandRaisedTracks] = useState<CustomTrack[]>([])
  const { hostAndCoHosts, host, coHost } = useGetHostAndCoHost()

  // Compute stagedParticipantsCount based on tracks
  const stagedParticipantsCount = useMemo(() => {
    return tracks.filter((track) => stagedParticipants.includes(track.participant.sid)).length
  }, [stagedParticipants, tracks])

  useEffect(() => {
    // Filter tracks that are for staged participants
    const newStagedTracks = tracks.filter(
      (track) => !hostAndCoHosts.includes(track.participant.sid) && stagedParticipants.includes(track.participant.sid)
    )
    setStagedTracks(newStagedTracks)

    // Filter tracks that are not host or co-host tracks and also not in stagedParticipants
    const otherTracksNonSorted = tracks.filter(
      (track) => !hostAndCoHosts.includes(track.participant.sid) && !stagedParticipants.includes(track.participant.sid)
    )

    // Sort hand-raised participants to be first
    const newOtherTracks = otherTracksNonSorted.sort((a, b) => {
      const aHandRaised = handRaisedParticipants.includes(a.participant.sid) ? 1 : 0
      const bHandRaised = handRaisedParticipants.includes(b.participant.sid) ? 1 : 0
      return bHandRaised - aHandRaised
    })

    setOtherTracks(newOtherTracks)

    // Filter tracks that are host or co-host tracks
    const newModeratorTracks = tracks.filter((track) => hostAndCoHosts.includes(track.participant.sid))
    const moderatorsFiltered = newModeratorTracks.sort((a, b) => {
      const aIsHost = host.includes(a.participant.sid) ? -1 : 0
      const bIsHost = host.includes(b.participant.sid) ? -1 : 0
      const aIsCoHost = coHost.includes(a.participant.sid) ? 1 : 0
      const bIsCoHost = coHost.includes(b.participant.sid) ? 1 : 0
      return aIsHost - bIsHost || aIsCoHost - bIsCoHost
    })
    setModeratorTracks(moderatorsFiltered)

    const newHandRaisedTracks = tracks.filter(
      (track) =>
        !hostAndCoHosts.includes(track.participant.sid) &&
        (stagedParticipants.includes(track.participant.sid) || handRaisedParticipants.includes(track.participant.sid))
    )
    setHandRaisedTracks(newHandRaisedTracks)
  }, [hostAndCoHosts, stagedParticipants, handRaisedParticipants, tracks])

  const otherCount = otherTracks.length

  return {
    stagedTracks,
    otherTracks,
    moderatorTracks,
    stagedParticipants,
    stagedParticipantsCount,
    moderatorCount: moderatorTracks.length,
    otherCount,
    handRaisedTracks,
    handraisedCount: handRaisedTracks.length,
  }
}

/**
 * A custom hook to check if there are multiple sessions with the same identity in a room.
 *
 * @return {object} An object containing a boolean value indicating whether there are multiple sessions with the same identity.
 */

export const useMultiSessionCheckHooks = () => {
  const remoteParticipants = useRemoteParticipants()
  const { localParticipant } = useLocalParticipant()

  const [isMultiSession, setIsMultiSession] = useState<boolean>(false)
  const [sameIdentity, setSameIdentity] = useState<boolean>(false)

  useEffect(() => {
    const localIdWithoutLastFourDigits = localParticipant?.identity.substring(
      0,
      localParticipant?.identity.lastIndexOf('-')
    )
    const hasOtherParticipants = remoteParticipants.length >= 1

    const otherParticipant = remoteParticipants.find((p) => {
      const remoteIdWithoutLastFourDigits = p.identity.substring(0, p.identity.lastIndexOf('-'))
      if (remoteIdWithoutLastFourDigits === localIdWithoutLastFourDigits) {
        setSameIdentity(true)
      } else {
        setSameIdentity(false)
      }
      return remoteIdWithoutLastFourDigits === localIdWithoutLastFourDigits
    })
    console.log('otherParticipant', otherParticipant)

    if (hasOtherParticipants && sameIdentity && !isMultiSession) {
      setIsMultiSession(true)
    } else if (!hasOtherParticipants || (!sameIdentity && isMultiSession)) {
      setIsMultiSession(false)
    }
  }, [remoteParticipants.length, localParticipant, sameIdentity, isMultiSession])
  return { isMultiSession }
}

/**
 * A custom hook to manage hand raise functionality in a LiveKit room.
 *
 * @return {object} An object containing hand raise functions and states.
 */

export const useHandRaiseHook = () => {
  const { send: sendHandRaise } = useDataChannel('handRaise')
  const { message: messageUnmute } = useDataChannel('allowUnmute')
  const [isAllowedUnmute, setIsAllowedUnmute] = useState<boolean>(false)
  const { localParticipant } = useLocalParticipant()
  const {
    setIsUnmuteAllowed,
    setStagedParticipants,
    stagedParticipants,
    isHostOrCohostAvailable,
    isHandRaised,
    setIsHandRaised,
  } = useCustomRoomContext()
  const [isHostUnmuted, setIsHostUnmuted] = useState<boolean>(false)

  const handRaise = (handRaised: boolean) => {
    const message: LiveKitHandRaised = {
      // currentUserId used for sending
      handRaised,
    }
    if (isHandRaised !== handRaised) {
      setIsHandRaised(handRaised)
    }

    const options: DataPublishOptions = {
      reliable: true,
    }
    sendHandRaise(new TextEncoder().encode(JSON.stringify(message)), options)
    setIsHostUnmuted(true)
  }

  const addOrRemoveAllowUnmuteParticipants = (participantId: string, allowUnmute: boolean) => {
    if (participantId) {
      if (allowUnmute) {
        setStagedParticipants([...stagedParticipants, participantId])
      } else {
        setStagedParticipants(stagedParticipants.filter((p) => p !== participantId).filter((p) => p !== ''))
      }
    }
  }

  useEffect(() => {
    let timer: string | number | NodeJS.Timeout | undefined
    if (!isHostOrCohostAvailable) {
      timer = setTimeout(() => {
        if (!isHostOrCohostAvailable) {
          setIsHandRaised(false)
        }
      }, 3000)
    }
    return () => clearTimeout(timer)
  }, [isHostOrCohostAvailable])

  useEffect(() => {
    if (messageUnmute) {
      const data = JSON.parse(new TextDecoder().decode(messageUnmute.payload)) as LiveKitEndAllowUnmute
      if (data.allowUnmute && data.participantId === localParticipant?.sid) {
        handRaise(false)
      }
      if (isAllowedUnmute !== data.allowUnmute && data.participantId === localParticipant?.sid) {
        setIsAllowedUnmute(data.allowUnmute)
        setIsUnmuteAllowed(data.allowUnmute)
      }
      addOrRemoveAllowUnmuteParticipants(data.participantId, data.allowUnmute)
    }
  }, [messageUnmute])

  return {
    handRaise,
    isHandRaised,
    isAllowedUnmute,
    isHostUnmuted,
    setIsHostUnmuted,
  }
}

/**
 * A custom hook to manage hand raise functionality in a LiveKit room.
 *
 * @return {object} An object containing hand raise state and functions to manage hand raise behavior.
 */

export const useHandRaiseReceivedHook = () => {
  const { message } = useDataChannel('handRaise')
  const { send: sendUnmute } = useDataChannel('allowUnmute')
  const { isLocalParticipantHost, isLocalParticipantCohost } = useGetIsLocalParticipantHostOrCohostHooks()
  const p = useEnsureParticipant()
  const {
    stagedParticipants,
    setStagedParticipants,
    handRaisedParticipants,
    setHandRaisedParticipants,
    isParticipantHandRaised,
    setIsParticipantHandRaised,
  } = useCustomRoomContext()
  const [participantName, setParticipantName] = useState<string>('')
  const [localHandRaised, setLocalHandRaised] = useState<boolean>(false)
  const { muteAllOthers } = useMuteAllHooks()

  useEffect(() => {
    if (!isLocalParticipantCohost && !isLocalParticipantHost) {
      return
    }
    if (message && message.payload?.byteLength > 0) {
      const data = JSON.parse(new TextDecoder().decode(message.payload)) as LiveKitHandRaised
      const participantId = message.from?.sid

      if (participantId) {
        if (data.handRaised) {
          setHandRaisedParticipants((prevParticipants: string[]) => [...prevParticipants, participantId])
        } else {
          setHandRaisedParticipants((prevParticipants: string[]) =>
            prevParticipants.filter((id) => id !== participantId)
          )
        }

        if (participantId === p.sid) {
          setIsParticipantHandRaised(data.handRaised)
          setLocalHandRaised(data.handRaised)
          setParticipantName(p.name ? p.name : '')
        }
      }
    }
  }, [message, p])

  const addAllParticipantsToStagedParticipants = useCallback(
    (participantId: any) => {
      if (!stagedParticipants.includes(participantId)) {
        setStagedParticipants([...stagedParticipants, participantId])
      }
    },
    [stagedParticipants.length, setStagedParticipants]
  )

  const muteAllOtherParticipants = (currentParticipantId: string) => {
    const otherParticipants = handRaisedParticipants.filter((id) => id !== currentParticipantId)
    otherParticipants.forEach((participantId) => {
      const message: LiveKitEndAllowUnmute = {
        allowUnmute: false,
        participantId: participantId,
      }
      sendUnmute(new TextEncoder().encode(JSON.stringify(message)), { reliable: true })
    })
    muteAllOthers()
  }

  const allowUnmute = () => {
    const message: LiveKitEndAllowUnmute = {
      // currentUserId used for sending
      allowUnmute: true,
      participantId: p.sid,
    }
    const options: DataPublishOptions = {
      reliable: true,
    }
    sendUnmute(new TextEncoder().encode(JSON.stringify(message)), options)
    addAllParticipantsToStagedParticipants(p.sid)
    setIsParticipantHandRaised(false)
    setLocalHandRaised(false)
    setHandRaisedParticipants(handRaisedParticipants.filter((id) => id !== p.sid))
  }

  const unmuteCurrentOnly = () => {
    muteAllOtherParticipants(p.sid)

    setTimeout(() => {
      const message: LiveKitEndAllowUnmute = {
        allowUnmute: true,
        participantId: p.sid,
      }
      const options: DataPublishOptions = {
        reliable: true,
      }
      sendUnmute(new TextEncoder().encode(JSON.stringify(message)), options)
      addAllParticipantsToStagedParticipants(p.sid)
      setIsParticipantHandRaised(false)
      setLocalHandRaised(false)
      setHandRaisedParticipants(handRaisedParticipants.filter((id) => id !== p.sid))
    }, 200)
  }

  return {
    isParticipantHandRaised,
    allowUnmute,
    unmuteCurrentOnly,
    participantName,
    handRaisedParticipants,
    setHandRaisedParticipants,
    localHandRaised,
  }
}

/**
 * A custom hook to manage mute all functionality in a LiveKit room.
 *
 * @return {object} An object containing mute all functions and states.
 */

export const useMuteAllHooks = () => {
  const { send: sendMuteAll, message: messageMuteAll } = useDataChannel('muteAll')
  const { send: sendMuteAllOthers, message: messageMuteAllOthers } = useDataChannel('muteAllOthers')
  const { isLocalParticipantCohost, isLocalParticipantHost } = useGetIsLocalParticipantHostOrCohostHooks()
  const isLocalHostAndCoHosts = isLocalParticipantCohost || isLocalParticipantHost
  const room = useRoomContext()
  const { setIsUnmuteAllowed, setStagedParticipants, setHandRaisedParticipants } = useCustomRoomContext()
  const [isMutedAll, setIsMutedAll] = useState<boolean>(false)
  const { setIsHostUnmuted, handRaise } = useHandRaiseHook()

  const muteAll = () => {
    const message = {
      muteAll: true,
    }
    const options: DataPublishOptions = {
      reliable: true,
    }

    sendMuteAll(new TextEncoder().encode(JSON.stringify(message)), options)
    setStagedParticipants([]) // This is for local user to empty staged participants
    setIsHostUnmuted(false)
    setHandRaisedParticipants([])
  }

  const muteAllOthers = () => {
    const message = {
      muteAll: true,
    }
    const options: DataPublishOptions = {
      reliable: true,
    }

    sendMuteAllOthers(new TextEncoder().encode(JSON.stringify(message)), options)
    setStagedParticipants([]) // This is for local user to empty staged participants
    setIsHostUnmuted(false)
  }

  useEffect(() => {
    if (isLocalHostAndCoHosts) {
      return
    }
    if (messageMuteAll && messageMuteAll.payload) {
      setStagedParticipants([]) // This is for remote user to empty staged participants
      room.localParticipant.setCameraEnabled(false)
      room.localParticipant.setMicrophoneEnabled(false)
      room.localParticipant.setScreenShareEnabled(false)
      setIsUnmuteAllowed(false)
      setIsHostUnmuted(false)
      setIsMutedAll(true)
      handRaise(false)
      setHandRaisedParticipants([])
    }
    if (messageMuteAllOthers && messageMuteAllOthers.payload) {
      setStagedParticipants([]) // This is for remote user to empty staged participants
      room.localParticipant.setCameraEnabled(false)
      room.localParticipant.setMicrophoneEnabled(false)
      room.localParticipant.setScreenShareEnabled(false)
      setIsUnmuteAllowed(false)
      setIsHostUnmuted(false)
      setIsMutedAll(true)
    }
  }, [messageMuteAll, messageMuteAllOthers, isLocalHostAndCoHosts, setIsUnmuteAllowed, setIsMutedAll, setIsHostUnmuted])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isMutedAll === true) {
        setIsMutedAll(false)
      }
    }, 3000)

    return () => clearTimeout(timeout)
  }, [isMutedAll])

  return {
    muteAll,
    isMutedAll,
    setIsMutedAll,
    muteAllOthers,
  }
}

/**
 * A custom hook to manage unmute all functionality in a LiveKit room.
 *
 * @return {object} An object containing unmute all functions and states.
 */

export const useUnMuteAllHooks = () => {
  const { send: sendUnmuteAll, message: messageUnmuteAll } = useDataChannel('unmuteAll')
  const { isLocalParticipantCohost, isLocalParticipantHost } = useGetIsLocalParticipantHostOrCohostHooks()
  const isLocalHostAndCoHosts = isLocalParticipantCohost || isLocalParticipantHost
  const { isUnmuteAllowed, setIsUnmuteAllowed } = useCustomRoomContext()
  const { handRaise } = useHandRaiseHook()
  const allParticipants = useParticipants()
  const { setStagedParticipants } = useCustomRoomContext()
  const { hostAndCoHosts } = useGetHostAndCoHost()
  const [isUnmutedAll, setIsUnmutedAll] = useState<boolean>(false)

  const addAllParticipantsToStagedParticipants = useCallback(() => {
    const otherParticipants = allParticipants
      .filter((p) => p.sid !== '' && !hostAndCoHosts.includes(p.sid))
      .map((p) => p.sid)
      .filter((p) => p !== '')

    setStagedParticipants(otherParticipants)
    setIsUnmutedAll(true)
  }, [allParticipants, hostAndCoHosts, setStagedParticipants])

  const unmuteAll = () => {
    const message = {
      unmuteAll: true,
    }

    const options: DataPublishOptions = {
      reliable: true,
    }
    sendUnmuteAll(new TextEncoder().encode(JSON.stringify(message)), options)
    addAllParticipantsToStagedParticipants()
  }

  useEffect(() => {
    if (isLocalHostAndCoHosts) {
      return
    }
    if (messageUnmuteAll && messageUnmuteAll.payload) {
      addAllParticipantsToStagedParticipants()
      if (!isUnmuteAllowed) {
        setIsUnmuteAllowed(true)
        handRaise(false)
      }
    }
  }, [messageUnmuteAll, setIsUnmuteAllowed])

  return {
    unmuteAll,
    isUnmuteAllowed,
    isUnmutedAll,
    setIsUnmutedAll,
  }
}

/**
 * A custom hook to manage host and co-host availability in a LiveKit room.
 *
 * @return {object} An object containing host/co-host availability state and the timestamp when the host/co-host became unavailable.
 */

export const useHostCoHostAvailabilityHook = () => {
  const participants = useParticipants()
  const { isHostOrCohostAvailable, setIsHostOrCohostAvailable, setStagedParticipants } = useCustomRoomContext()
  const [hostNotAvailableSinceDateTime, setHostNotAvailableSinceDateTime] = useState<string | undefined>(undefined)
  const { muteAll } = useMuteAllHooks()

  const setHostNotAvailable = () => {
    if (isHostOrCohostAvailable) {
      setIsHostOrCohostAvailable(false)
      setHostNotAvailableSinceDateTime(new Date().toISOString())
    }
  }
  useEffect(() => {
    if (!isHostOrCohostAvailable) {
      setHostNotAvailableSinceDateTime(new Date().toISOString())
    }
  }, [isHostOrCohostAvailable])

  useEffect(() => {
    if (!participants || participants.length === 1) {
      setHostNotAvailable()
      return
    }

    const remoteParticipants = participants.filter((p) => p instanceof RemoteParticipant)
    if (remoteParticipants.length === 0) {
      return
    }

    const host = participants.find((p) => {
      if (p.metadata) {
        try {
          const metadata = JSON.parse(p.metadata)
          return metadata.userType === UserType.HOST || metadata.userType === UserType.CO_HOST
        } catch (e) {
          return false
        }
      }
      return false
    })

    if (host) {
      if (!isHostOrCohostAvailable) {
        setIsHostOrCohostAvailable(true)
        setHostNotAvailableSinceDateTime(undefined)
      }
    } else if (!host && participants.length > 0) {
      setHostNotAvailable()
      setStagedParticipants([])
      muteAll()
    }
  }, [participants.length])

  return {
    isHostOrCohostAvailable,
    hostNotAvailableSinceDateTime,
  }
}

/**
 * A custom hook to manage the meeting start timer in a LiveKit room.
 *
 * @return {object} An object containing:
 *   - isShowStarted: A boolean indicating whether the meeting has started.
 *   - formattedStartTime: A string representing the formatted meeting start time.
 *   - countdown: A string representing the countdown to the meeting start time, or null if the countdown is not active.
 */

export const useMeetingStartTimerHook = () => {
  const { meetingStartTime } = useCustomRoomContext()
  const [countdown, setCountdown] = useState<string | null>(null)

  const isShowStarted = useMemo(() => {
    if (!meetingStartTime) return false
    return new Date() >= new Date(meetingStartTime)
  }, [meetingStartTime])

  const formattedStartTime = useMemo(() => {
    if (!meetingStartTime) return ''

    const date = new Date(meetingStartTime)
    const now = new Date()

    const timeDiffMs = date.getTime() - now.getTime()
    const timeDiffMins = Math.floor(timeDiffMs / 60000)

    if (timeDiffMs > 0 && timeDiffMins < 15) {
      const totalSeconds = Math.floor(timeDiffMs / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    const meetingDay = date.getDay()
    const meetingDate = date.getDate()
    const meetingMonth = date.getMonth()
    const meetingYear = date.getFullYear()

    const nowDate = now.getDate()
    const nowMonth = now.getMonth()
    const nowYear = now.getFullYear()

    const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    let dateString = ''

    if (meetingDate === nowDate && meetingMonth === nowMonth && meetingYear === nowYear) {
      dateString = 'Today'
    } else if (meetingDate === nowDate + 1 && meetingMonth === nowMonth && meetingYear === nowYear) {
      dateString = 'Tomorrow'
    } else if (diffDays > 1 && diffDays <= 6) {
      dateString = weekdayNames[meetingDay]
    } else {
      const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
      dateString = date.toLocaleDateString(undefined, options)
    }

    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const formattedHours = hours % 12 || 12
    const formattedMinutes = minutes.toString().padStart(2, '0')
    const timeString = `${formattedHours}.${formattedMinutes} ${ampm}`

    return `${dateString} ${timeString}`
  }, [meetingStartTime])

  // Countdown timer effect
  useEffect(() => {
    if (!meetingStartTime) return

    const interval = setInterval(() => {
      const now = new Date()
      const start = new Date(meetingStartTime)
      const diffMs = start.getTime() - now.getTime()

      if (diffMs > 0 && diffMs < 15 * 60 * 1000) {
        const totalSeconds = Math.floor(diffMs / 1000)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        setCountdown(`${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`)
      } else {
        setCountdown(null)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [meetingStartTime])

  return {
    isShowStarted,
    formattedStartTime,
    countdown,
  }
}

/**
 * A custom hook to manage the meeting end timer in a LiveKit room.
 * It calculates the time remaining until the meeting ends and triggers alerts at specified intervals.
 *
 * @return {object} An object containing the time left string, and flags to show ping, 5-minute alert, and 2-minute alert.
 */

export const useMeetingEndTimerHook = () => {
  const {
    waitingTimeForHostOrCoHost,
    meetingEndTime,
    alertsBeforeMeetingEnds,
    isHostOrCohostAvailable,
    setShouldLeaveMeeting,
  } = useCustomRoomContext()
  const { hostNotAvailableSinceDateTime } = useHostCoHostAvailabilityHook()
  const { state: connectionState } = useRoomContext()
  const participants = useParticipants()

  const [timeLeftString, setTimeLeftString] = useState<string>()
  const [showPing, setShowPing] = useState<boolean>(false)
  const [showAlertFiveMin, setShowAlertFiveMin] = useState(false)
  const [twoMinAlert, setTwoMinAlert] = useState(false)

  const hostOrCoHost = participants.find((p) => {
    if (p.metadata) {
      try {
        const metadata = JSON.parse(p.metadata)
        return metadata.userType === UserType.HOST || metadata.userType === UserType.CO_HOST
      } catch (e) {
        return false
      }
    }
    return false
  })

  useEffect(() => {
    let timer: any
    let endTime = new Date(meetingEndTime)
    if (meetingEndTime && connectionState === ConnectionState.Connected) {
      timer = setInterval(() => {
        if (!isHostOrCohostAvailable && hostNotAvailableSinceDateTime && !hostOrCoHost) {
          const hostNotAvailableEndTime = addSecondsToDate(
            new Date(hostNotAvailableSinceDateTime),
            waitingTimeForHostOrCoHost
          )
          endTime = endTime < hostNotAvailableEndTime ? endTime : hostNotAvailableEndTime
        }
        const { timeLeftString, minutes, timeLeftInSeconds } = calculateTimeRemaining(endTime)
        setTimeLeftString(timeLeftString)
        setShowPing(minutes < 5)

        // if timeleft in seconds is 300, show alert
        if (
          alertsBeforeMeetingEnds.includes(timeLeftInSeconds) &&
          timeLeftInSeconds === alertsBeforeMeetingEnds[0] &&
          !showAlertFiveMin
        ) {
          setShowAlertFiveMin(true)
          setTimeout(() => {
            setShowAlertFiveMin(false)
          }, 5000)
        }
        if (
          alertsBeforeMeetingEnds.includes(timeLeftInSeconds) &&
          timeLeftInSeconds === alertsBeforeMeetingEnds[1] &&
          !twoMinAlert
        ) {
          setTwoMinAlert(true)
          setTimeout(() => {
            setTwoMinAlert(false)
          }, 5000)
        }

        if (timeLeftInSeconds <= 0) {
          setShouldLeaveMeeting(true)
        }
      }, 1000)
    }

    return () => clearInterval(timer)
  }, [
    connectionState,
    isHostOrCohostAvailable,
    hostNotAvailableSinceDateTime,
    meetingEndTime,
    alertsBeforeMeetingEnds,
    showAlertFiveMin,
    twoMinAlert,
    waitingTimeForHostOrCoHost,
    setShouldLeaveMeeting,
    hostOrCoHost,
  ])

  return {
    timeLeftString,
    showPing,
    showAlertFiveMin,
    twoMinAlert,
  }
}

/**
 * A custom hook to check audio and video permissions in a browser.
 *
 * @return {object} An object containing the state of audio and video permissions.
 */

export const usePermissionCheckHooks = () => {
  const [isAudioPermissionGrantedError, setIsAudioPermissionGrantedError] = useState<boolean>(false)
  const [isVideoPermissionGrantedError, setIsVideoPermissionGrantedError] = useState<boolean>(false)

  async function checkAudioPermissions() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((track) => track.stop())
      setIsAudioPermissionGrantedError(false)
    } catch (error: any) {
      console.error(error)
      if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
        setIsAudioPermissionGrantedError(true)
      }
    }
  }
  async function checkVideoPermissions() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach((track) => track.stop())
      setIsVideoPermissionGrantedError(false)
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
        setIsVideoPermissionGrantedError(true)
      }
    }
  }
  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', checkAudioPermissions)
    navigator.mediaDevices.addEventListener('devicechange', checkVideoPermissions)
  }, [])

  useEffect(() => {
    if (isAudioPermissionGrantedError) {
      return
    }
    checkAudioPermissions()
    checkVideoPermissions()
  }, [isAudioPermissionGrantedError, isVideoPermissionGrantedError])

  return {
    isAudioPermissionGrantedError,
    isVideoPermissionGrantedError,
  }
}

/**
 * A custom hook to check if a host or co-host is available in the current room.
 *
 * @return {object} An object containing a boolean indicating whether a host or co-host is available.
 */

export const useControlBarAccessHooks = () => {
  const participants = useParticipants()

  const isHostOrCoHostAvailableRoom = participants.some((participant) => {
    if (participant instanceof LocalParticipant && participant.metadata) {
      const userType = JSON.parse(participant.metadata).userType
      return userType === UserType.HOST || userType === UserType.CO_HOST
    }
    return false
  })

  return {
    isHostOrCoHostAvailableRoom,
  }
}

/**
 * A custom hook for detecting outside clicks on a specified element.
 *
 * @param {object} options - Options for the hook.
 * @param {boolean} options.initialValue - The initial state of the outside click detection.
 * @return {object} An object containing the ref to attach to the element, the current state of the outside click detection, and a function to toggle the state.
 */

export const useOutsideClickDetectionHook = ({ initialValue }: { initialValue: boolean }) => {
  const [isParticipantMenuOpen, setIsParticipantMenuOpen] = useState(initialValue)
  const ref = useRef<any>(null)

  const handleClickOutside = (event: any) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsParticipantMenuOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  return { ref, isParticipantMenuOpen, setIsParticipantMenuOpen }
}

/**
 * A custom hook to handle ending a LiveKit room.
 *
 * @return {object} An object containing the endRoom function, which sends an end room message to all participants and sets the isHostEnded state to true after a 5-second delay.
 */

export const useEndRoomHooks = () => {
  const { send: sendEndRoom } = useDataChannel('endRoom')
  const { localParticipant } = useLocalParticipant()
  const { setIsHostEnded } = useCustomRoomContext()

  const message: LiveKitEndRoom = {
    currentUserId: localParticipant?.sid,
  }

  const endRoom = () => {
    const options: DataPublishOptions = {
      reliable: true,
    }
    sendEndRoom(new TextEncoder().encode(JSON.stringify(message)), options)
    setTimeout(() => {
      setIsHostEnded(true)
    }, 5000)
  }

  return { endRoom }
}

/**
 * A custom hook to receive end room messages.
 *
 * @return {object} An object containing a boolean indicating whether the room has ended.
 */

export const useEndRoomReceiver = () => {
  const { message } = useDataChannel('endRoom')
  const [isRoomEnded, setIsRoomEnded] = useState(false)

  useEffect(() => {
    if (message && message.payload) {
      const decodedMessage = JSON.parse(new TextDecoder().decode(message.payload)) as LiveKitEndRoom
      if (decodedMessage) {
        setIsRoomEnded(true)
      }
    }
  }, [message])

  return { isRoomEnded }
}

/**
 * A custom hook to handle ending a LiveKit live shopping room.
 *
 * @return {object} An object containing the sendEndLiveShoppingState function, which sends an end live shopping message to all participants and sets the endLiveShopping state to true after a 3.5-second delay.
 */
export const useLiveShoppingEndRoom = () => {
  const { send: sendEndLiveShopping } = useDataChannel('endLiveShopping')
  const { setEndLiveShopping } = useCustomRoomContext()
  const { localParticipant } = useLocalParticipant()

  const sendEndLiveShoppingState = useCallback(() => {
    const isHostOrCohost =
      JSON.parse(localParticipant?.metadata ?? '{}').userType === UserType.HOST ||
      JSON.parse(localParticipant?.metadata ?? '{}').userType === UserType.CO_HOST
    if (!isHostOrCohost) {
      return
    }
    const message = {
      endLiveShopping: true,
    }
    const options: DataPublishOptions = {
      reliable: true,
    }
    const encodedMessage = new TextEncoder().encode(JSON.stringify(message))
    // setTimeout(() => {
    sendEndLiveShopping(encodedMessage, options)
    setEndLiveShopping(true)
    // }, 3500)
  }, [localParticipant?.metadata, sendEndLiveShopping, setEndLiveShopping])

  return { sendEndLiveShoppingState }
}

/**
 * A custom hook to receive end live shopping messages in a LiveKit room.
 *
 * @return {void} This hook does not return any values.
 */
export const useReceiveLiveShoppingEndRoom = () => {
  const { message } = useDataChannel('endLiveShopping')
  const { setEndLiveShopping } = useCustomRoomContext()

  useEffect(() => {
    if (!message) {
      return
    }
    const messageData = JSON.parse(new TextDecoder().decode(message.payload))
    if (messageData.endLiveShopping) {
      setEndLiveShopping(true)
    }
  }, [message, setEndLiveShopping])
}

/**
 * Returns the current dimensions of the browser window.
 *
 * @return {object} An object containing the width and height of the window.
 */

export function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

/**
 * A custom hook for getting and updating the current window dimensions.
 *
 * @return {object} An object containing the current window dimensions.
 */

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowDimensions
}

/**
 * A custom hook to get the current screen dimensions and grid wrapper reference.
 *
 * @param {TrackReferenceOrPlaceholder[]} tracks - An array of track references or placeholders.
 * @return {object} An object containing the grid wrapper reference, grid dimensions, scaled pixel ratio, screen height, and screen width.
 */

export const useScreenDimensions = (tracks: TrackReferenceOrPlaceholder[]) => {
  const gridWrapperRef = createRef<HTMLDivElement>()
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const { gridDimensions, setGridDimensions } = useCustomRoomContext()

  const scaledPixelRatio = window.devicePixelRatio

  useEffect(() => {
    if (gridWrapperRef.current) {
      if (
        gridDimensions.width !== gridWrapperRef.current.offsetWidth ||
        gridDimensions.height !== gridWrapperRef.current.offsetHeight / 1.1
      ) {
        setGridDimensions({
          width: gridWrapperRef.current.offsetWidth,
          height: gridWrapperRef.current.offsetHeight / 1.1,
        })
      }
    }
  }, [screenHeight, screenWidth, gridWrapperRef, gridDimensions])

  return {
    gridWrapperRef,
    gridDimensions,
    scaledPixelRatio,
    screenHeight,
    screenWidth,
  }
}

interface StagedParticipantsMessage {
  stagedParticipants: string[]
}

/**
 * A custom hook to send a list of staged participants to a remote participant.
 *
 * @return {void}
 */

export const useSendStagedParticipantsList = () => {
  const { send: sendStagedParticipantsList } = useDataChannel('stagedParticipantsList')
  const room = useEnsureRoom()
  const { stagedParticipants } = useCustomRoomContext()
  const { localParticipant } = useLocalParticipant()

  const sendStagedParticipants = useCallback(
    (participant: RemoteParticipant) => {
      const isHostOrCohost =
        JSON.parse(localParticipant?.metadata ?? '{}').userType === UserType.HOST ||
        JSON.parse(localParticipant?.metadata ?? '{}').userType === UserType.CO_HOST
      if (!isHostOrCohost) {
        return
      }
      const message: StagedParticipantsMessage = {
        stagedParticipants,
      }
      const options: DataPublishOptions = {
        reliable: true,
        destinationIdentities: [participant.identity],
      }
      const encodedMessage = new TextEncoder().encode(JSON.stringify(message))
      setTimeout(() => {
        sendStagedParticipantsList(encodedMessage, options)
      }, 3500)
    },
    [localParticipant?.metadata, sendStagedParticipantsList, stagedParticipants]
  )

  useEffect(() => {
    room?.addListener('participantConnected', (newParticipant) => {
      sendStagedParticipants(newParticipant)
    })
  }, [room, sendStagedParticipants])
}

/**
 * A custom hook to receive a list of staged participants from a remote participant.
 *
 * @return {void}
 */

export const useReceiveStagedParticipantsList = () => {
  const { message } = useDataChannel('stagedParticipantsList')
  const { setStagedParticipants } = useCustomRoomContext()
  useEffect(() => {
    if (!message) {
      return
    }
    const messageData = JSON.parse(new TextDecoder().decode(message.payload))
    setStagedParticipants(messageData.stagedParticipants)
  }, [message, setStagedParticipants])
}

/**
 * A custom hook to get the current user position in a LiveKit room.
 *
 * @param {TrackReference | TrackReferenceOrPlaceholder} trackReferene - The track reference or placeholder to determine the user position for.
 * @return {object} An object containing the current user's pinned and unpinned positions, as well as counts of pinned and unpinned participants and camera-enabled users.
 */

export const useGetUserPosition = (trackReferene: TrackReference | TrackReferenceOrPlaceholder) => {
  const [userPinnedPosition, setUserPinnedPosition] = useState(-1) // current user position
  const [userUnpinnedPosition, setUserUnpinnedPosition] = useState<number>(-1) // current user position
  const [unPinnedCount, setUnPinnedCount] = useState<number>(0)
  const [camEnabledUserCount, setCamEnabledUserCount] = useState<number>(0)

  const allParticipants = useParticipants()
  const participant = useEnsureParticipant()
  const { pinnedParticipants } = useCustomGridContext()
  const { viewType, stagedParticipants, handRaisedParticipants, allTracksSids, allTracks } = useCustomRoomContext()
  const { hostAndCoHosts } = useGetHostAndCoHost()

  // console.log('pinnedParticipants ----->', pinnedParticipants)

  const sidSource = trackReferene?.publication?.trackSid ?? trackReferene.participant.sid
  const pinnedParticipantTrack = allTracksSids.filter((p) => pinnedParticipants.includes(p))
  useEffect(() => {
    if (pinnedParticipantTrack.length === 0) {
      setUserPinnedPosition(-1)
    } else {
      setUserPinnedPosition(pinnedParticipantTrack.findIndex((p) => p === sidSource))
    }
  }, [pinnedParticipantTrack, sidSource, allTracks])

  useEffect(() => {
    if (allTracksSids.length === 0) {
      setUserUnpinnedPosition(-1)
      setCamEnabledUserCount(0)
    } else {
      let unPinnedParticipants = allTracksSids.filter((p) => !pinnedParticipants.includes(p))

      const cameraEnabledParticipants = allParticipants
        .filter((p) => p.isCameraEnabled || p.isScreenShareEnabled)
        .map((p) => p.sid)
      const screenSharedTracks = allTracks
        .filter((track) => track.source === Track.Source.ScreenShare)
        .map((track) => track.publication?.trackSid ?? track.participant.sid + '-' + track?.source)

      if (viewType === 'hostHighlighted') {
        if (unPinnedParticipants.length > 0) {
          unPinnedParticipants = unPinnedParticipants.filter(
            (p) =>
              handRaisedParticipants.includes(p) ||
              screenSharedTracks.includes(p) ||
              cameraEnabledParticipants.includes(p) ||
              hostAndCoHosts.includes(p)
          )
        }

        if (screenSharedTracks.length > 0 || hostAndCoHosts.length > 0) {
          unPinnedParticipants = [
            ...unPinnedParticipants.filter((p) => !screenSharedTracks.includes(p) && !hostAndCoHosts.includes(p)), // other unpinned participants
            ...screenSharedTracks.filter((p: string) => !pinnedParticipants.includes(p)),
            ...hostAndCoHosts.filter((p) => !pinnedParticipants.includes(p)),
          ]
        }
      }
      setUserUnpinnedPosition(unPinnedParticipants.findIndex((p) => p === sidSource))
      setUnPinnedCount(unPinnedParticipants.length)
      setCamEnabledUserCount(cameraEnabledParticipants.length)
    }
  }, [
    allTracksSids,
    participant?.sid,
    pinnedParticipants,
    handRaisedParticipants,
    stagedParticipants,
    viewType,
    allParticipants,
  ])

  return {
    userPinnedPosition,
    userUnpinnedPosition,
    pinnedParticipantCount: pinnedParticipantTrack.length,
    unpinnedParticipantCount: unPinnedCount,
    camEnabledUserCount,
  }
}

/**
 * Calculates and returns the tile position of a participant in a LiveKit room.
 *
 * @param {TrackReference | TrackReferenceOrPlaceholder} trackReference - The track reference or placeholder to determine the user position for.
 * @return {object} An object containing the user position, full name, round size, isHiddenParticipant, addPinnedTrackOrParticipant, and removePinnedTrackOrParticipant.
 */

export const useParticipantTilePosition = (trackReference: TrackReference | TrackReferenceOrPlaceholder) => {
  const { name } = useParticipantInfo()
  const { gridDimensions, viewType, allTracks } = useCustomRoomContext()
  const [isHiddenParticipant, setIsHiddenParticipant] = useState<boolean>(false)
  const { addPinnedTrackOrParticipant, removePinnedTrackOrParticipant } = useCustomGridContext()
  const { userPinnedPosition, userUnpinnedPosition, pinnedParticipantCount, unpinnedParticipantCount } =
    useGetUserPosition(trackReference)

  const [userPosition, setUserPosition] = useState<UserLayoutPosition>({
    w: 0,
    h: 0,
    row: 0,
    col: 0,
    top: 0,
    left: 0,
  })
  const roundSize = Math.min(userPosition.h, userPosition.w)
  const fullName = name ? name.replace(/\b\w/g, (c) => c.toUpperCase()) : ''

  const prevParticipantsRef = useRef(allTracks)

  useEffect(() => {
    const prevParticipants = prevParticipantsRef.current
    if (prevParticipants.length !== allTracks.length) {
      const leftParticipants = prevParticipants.filter(
        (participant: TrackReferenceOrPlaceholder) =>
          !allTracks.some((p: TrackReferenceOrPlaceholder) => p.participant.sid === participant.participant.sid)
      )
      leftParticipants.forEach((participant: TrackReferenceOrPlaceholder) =>
        removePinnedTrackOrParticipant(participant.participant.sid)
      )
    }
    prevParticipantsRef.current = allTracks
  }, [allTracks, removePinnedTrackOrParticipant])

  useEffect(() => {
    const rightGridWidth = gridDimensions.width / 5 < 150 ? 150 : gridDimensions.width / 5
    const centerGridWidth =
      (pinnedParticipantCount > 0 && unpinnedParticipantCount === 0) ||
      (pinnedParticipantCount === 0 && unpinnedParticipantCount > 0)
        ? gridDimensions.width
        : gridDimensions.width - rightGridWidth

    const isCenter =
      pinnedParticipantCount + unpinnedParticipantCount === 1 ||
      (pinnedParticipantCount > 0 && unpinnedParticipantCount === 0) ||
      (pinnedParticipantCount === 0 && unpinnedParticipantCount > 0) ||
      (pinnedParticipantCount > 0 && unpinnedParticipantCount > 0 && userPinnedPosition > -1)

    const size = isCenter
      ? getGridAspect(
          100,
          centerGridWidth * 0.98,
          gridDimensions.height,
          pinnedParticipantCount || unpinnedParticipantCount,
          centerGridWidth,
          '16:9'
        )
      : getGridAspect(
          100,
          rightGridWidth * 0.98,
          gridDimensions.height,
          unpinnedParticipantCount,
          rightGridWidth,
          '16:9'
        )

    if (size && isCenter) {
      const userLayoutPositions = calculateBoxPositionsForEachUser(
        size.boxH,
        size.boxW,
        pinnedParticipantCount || unpinnedParticipantCount,
        centerGridWidth,
        gridDimensions.height,
        0
      )

      const position = userPinnedPosition > -1 ? userPinnedPosition : userUnpinnedPosition
      if (userLayoutPositions[position]) {
        setUserPosition(userLayoutPositions[position])
      }
    } else if (size && !isCenter) {
      const userLayoutPositions = calculateBoxPositionsForEachUser(
        size.boxH,
        size.boxW,
        unpinnedParticipantCount,
        rightGridWidth,
        gridDimensions.height,
        0,
        0
        // centerGridWidth
      )

      if (userLayoutPositions[userUnpinnedPosition]) {
        const positionSet = userLayoutPositions[userUnpinnedPosition]
        positionSet.left = positionSet.left + centerGridWidth
        setUserPosition(positionSet)
      }
    }
  }, [
    gridDimensions.height,
    gridDimensions.width,
    pinnedParticipantCount,
    unpinnedParticipantCount,
    userPinnedPosition,
    userUnpinnedPosition,
    viewType,
  ])

  useEffect(() => {
    if (userPinnedPosition < 0 && userUnpinnedPosition < 0) {
      setIsHiddenParticipant(true)
    } else {
      setIsHiddenParticipant(false)
    }
  }, [userPinnedPosition, userUnpinnedPosition])

  return {
    userPosition,
    fullName,
    roundSize,
    isHiddenParticipant,
    addPinnedTrackOrParticipant,
    removePinnedTrackOrParticipant,
    pinnedParticipantCount,
    unpinnedParticipantCount,
  }
}

/**
 * Send a message to update the bid amount for all participants.
 * @param {{ type: 'BID_UPDATE', bidAmount: number }} message - The message to send to update the bid amount.
 * @returns {object} An object with a single method, `updateBidAmount`, which sends the message.
 */
export function useUpdateBidAmount() {
  const room = useRoomContext()
  const { send: sendBidUpdate } = useDataChannel('bid_update')

  const updateBidAmount = (message: object) => {
    if (!room) {
      return
    }
    const payload = JSON.stringify(message)
    sendBidUpdate(new TextEncoder().encode(payload), { reliable: true })
  }

  return {
    updateBidAmount,
  }
}

// export function useBidChannel() {
//   const room = useRoomContext()
//   const { localParticipant } = useLocalParticipant()
//   // const { bidAmount } = useCustomRoomContext()

//   const sendBid: (bidAmount: number, sender: string) => void = (bidAmount, sender) => {
//     const message: BidMessage = {
//       bidAmount,
//       sender: localParticipant?.sid,
//     }
//     const options: DataPublishOptions = {
//       reliable: true,
//     }
//     const encodedMessage = new TextEncoder().encode(JSON.stringify(message))
//     room.localParticipant.publishData(encodedMessage, options)
//   }

//   return {
//     sendBid,
//   }
// }

/**
 * A custom hook to manage the initial bid amount of a LiveKit live shopping room.
 *
 * This hook is used to reset the initial bid amount of the room when a new product is selected.
 *
 * @return {object} An object containing a boolean value indicating whether the initial bid has been changed, and a setter to update the value.
 */
export const useResetLiveShopping = () => {
  const { receivedProductId } = useReceiveProductId()
  const [initialChanged, setInitialChanged] = useState(false)
  const { setBidAmount, productId, initialBid } = useCustomRoomContext()
  useEffect(() => {
    if (receivedProductId || productId !== '') {
      if (!productId && !receivedProductId) {
        setBidAmount(initialBid)
      }
      setInitialChanged(true)
    }
  }, [receivedProductId, productId])

  return { initialChanged, setInitialChanged }
}

/**
 * A custom hook to send a bid amount to all participants in a LiveKit live shopping room.
 *
 * This hook is used to send a bid amount to all participants in the room.
 *
 * @return {object} An object containing a single method, `sendBidAmount`, which sends a bid amount to all participants in the room.
 */
export const useSendBid = () => {
  const { send: sendBid } = useDataChannel('bidding')
  const { localParticipant } = useLocalParticipant()

  const sendBidAmount = (bidAmount: number) => {
    const bid: LiveKitBid = {
      userId: localParticipant?.sid,
      bidAmount,
      sender: localParticipant?.name,
      find: function (arg0: (bid: { userId: string }) => boolean): unknown {
        throw new Error('Function not implemented.')
      },
    }
    const options: DataPublishOptions = { reliable: true }
    sendBid(new TextEncoder().encode(JSON.stringify(bid)), options)
  }

  return { sendBidAmount }
}

/**
 * A custom hook for managing host bidding in a LiveKit live shopping room.
 *
 * This hook listens for bid messages over a data channel and maintains a list of bids.
 * It also provides functionality to accept a bid, which updates the accepted bid details
 * and sends a response to participants.
 *
 * @return {object} An object containing:
 * - `bids`: An array of `LiveKitBid` objects representing the current bids.
 * - `acceptBid`: A function to accept a bid from a specific user by user ID.
 * - `acceptedBidAmount`: The amount of the accepted bid.
 * - `acceptedName`: The name of the participant whose bid was accepted.
 */

export const useHostBidding = () => {
  const { message, send: sendHostResponse } = useDataChannel('bidding')
  const [bids, setBids] = useState<LiveKitBid[]>([])
  const {
    acceptedBidAmount,
    setAcceptedBidAmount,
    acceptedName,
    setAcceptedName,
    setAcceptedUserSid,
    setFinalBid,
    receivedProductId,
  } = useCustomRoomContext()

  useEffect(() => {
    if (message?.payload) {
      const newBid = JSON.parse(new TextDecoder().decode(message.payload)) as LiveKitBid

      setBids((prevBids) => {
        const updatedBids = [...prevBids]
        const existingBidIndex = updatedBids.findIndex((bid) => bid.userId === newBid.userId)

        if (existingBidIndex >= 0) {
          updatedBids[existingBidIndex] = newBid
        } else {
          updatedBids.push(newBid)
        }

        return updatedBids
      })
    }
  }, [message])

  useEffect(() => {
    setBids([])
    setAcceptedBidAmount(null)
    setAcceptedName('')
    setAcceptedUserSid('')
    setFinalBid(0)
  }, [receivedProductId])

  const acceptBid = (acceptedUserId: string) => {
    const acceptedBid = bids.find((bid) => bid.userId === acceptedUserId)
    const acceptedName = bids.find((bid) => bid.userId === acceptedUserId)?.sender

    if (!acceptedBid) {
      console.error(`No bid found for userId: ${acceptedUserId}`)
      return
    }

    setFinalBid(acceptedBid.bidAmount)
    setAcceptedBidAmount(acceptedBid.bidAmount)
    setAcceptedUserSid(acceptedUserId)
    setAcceptedName(acceptedName)

    const options: DataPublishOptions = { reliable: true }
    const messages: HostResponse[] = [
      {
        userId: acceptedUserId,
        message: `Host accepted your bid of ${acceptedBid.bidAmount} for ${acceptedName}`,
      },
      {
        userId: 'ALL',
        message: `Bidding is completed. ${acceptedName} won with a bid of ${acceptedBid.bidAmount}`,
      },
    ]

    sendHostResponse(new TextEncoder().encode(JSON.stringify(messages)), options)
  }

  return { bids, acceptBid, acceptedBidAmount, acceptedName }
}

/**
 * A custom hook to receive bid update messages in a LiveKit room.
 *
 * This hook listens for incoming bid update messages over a data channel.
 * Upon receiving a valid message, it updates the bid amount and the last sent bid
 * in the custom room context.
 *
 * @return {object} An empty object as the hook does not expose any methods or state.
 */

export const useReceiveBidUpdate = () => {
  const { message } = useDataChannel('bid_update')
  const { setBidAmount, setLastSentBid } = useCustomRoomContext()

  useEffect(() => {
    if (message?.payload) {
      const decodedData = new TextDecoder().decode(message.payload)
      try {
        const update = JSON.parse(decodedData)
        if (update.type === 'BID_UPDATE') {
          setBidAmount(update.bidAmount)
          setLastSentBid(update.bidAmount)
        }
      } catch (error) {
        console.error('useReceiveBidUpdate - Error parsing bid update message:', error)
      }
    }
  }, [message, setBidAmount, setLastSentBid])

  return {}
}

/**
 * A custom hook to receive bidding status messages in a LiveKit room.
 *
 * This hook listens for incoming bidding status messages over a data channel.
 * Upon receiving a valid message, it updates the status message and resets the status message when the product changes.
 *
 * @return {object} An object containing the status message and a setter to update it.
 */
export const useReceiveBiddingStatus = () => {
  const { message } = useDataChannel('bidding')
  const { localParticipant } = useLocalParticipant()
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const { receivedProductId } = useCustomRoomContext()

  // console.log('called inside useReceiveBiddingStatus')

  useEffect(() => {
    if (message && message.payload) {
      const decodedData = new TextDecoder().decode(message.payload)
      let messages: HostResponse[] = []

      try {
        const parsedData = JSON.parse(decodedData)

        messages = Array.isArray(parsedData) ? parsedData : [parsedData]
      } catch (error) {
        console.error('Error parsing bidding message:', error)
        return
      }

      const relevantMessage = messages.find((msg) => msg.userId === localParticipant?.sid || msg.userId === 'ALL')

      if (relevantMessage) {
        setStatusMessage(relevantMessage.message)
      }
    }
  }, [message])

  useEffect(() => {
    setStatusMessage(null)
  }, [receivedProductId])

  return { statusMessage, setStatusMessage }
}

/**
 * A custom hook to send a product ID to all participants in a LiveKit room.
 *
 * This hook listens for changes to the product ID in the custom room context and sends
 * the product ID to all participants in the room when it changes.
 *
 * @return {object} An object containing a function to manually send the product ID to all participants.
 */
export const useSendProductId = () => {
  const { send: sendProductId } = useDataChannel('product')
  const { productId } = useCustomRoomContext()
  const { localParticipant } = useLocalParticipant()

  const isHostOrCohost =
    JSON.parse(localParticipant?.metadata ?? '{}').userType === UserType.HOST ||
    JSON.parse(localParticipant?.metadata ?? '{}').userType === UserType.CO_HOST

  const sendProductIdToAll = () => {
    if (!isHostOrCohost || !productId) {
      return
    }

    const productMessage = {
      productId,
    }

    const options: DataPublishOptions = {
      reliable: true,
    }

    sendProductId(new TextEncoder().encode(JSON.stringify(productMessage)), options)
  }

  // Automatically send productId when it changes
  useEffect(() => {
    sendProductIdToAll()
  }, [productId])

  return { sendProductIdToAll }
}

/**
 * A custom hook to receive product ID messages in a LiveKit room.
 *
 * This hook listens for incoming product ID messages over a data channel.
 * Upon receiving a valid message, it decodes the product ID and updates
 * the received product ID in the custom room context.
 *
 * @return {object} An object containing the `receivedProductId`.
 */

export const useReceiveProductId = () => {
  const { message } = useDataChannel('product')
  const { receivedProductId, setReceivedProductId } = useCustomRoomContext()

  useEffect(() => {
    if (message && message.payload) {
      try {
        if (!(message.payload instanceof Uint8Array)) {
          return
        }

        // console.log('received productId', message.payload)

        const decodedData = new TextDecoder().decode(message.payload)

        const productMessage = JSON.parse(decodedData)

        if (productMessage.productId) {
          setReceivedProductId(productMessage.productId)
        }
      } catch (error) {
        console.error('Error decoding productId message:', error)
      }
    }
  }, [message])

  return { receivedProductId }
}

/**
 * A custom hook to send a 'like' message to all participants in a LiveKit room.
 *
 * This hook provides a function, `sendLikeToAll`, that sends a 'like' message to all
 * participants in the room. The message is a JSON object with two properties:
 * `userId` and `action`. `userId` is set to the sid of the local participant, and
 * `action` is set to either 'like' or 'unlike', depending on the value of the
 * `isLiked` parameter passed to `sendLikeToAll`.
 *
 * @return {object} An object containing the `sendLikeToAll` function.
 */
export const useSendLike = () => {
  const { send: sendLike } = useDataChannel('like')
  const { localParticipant } = useLocalParticipant()

  const sendLikeToAll = (isLiked: boolean) => {
    const userId = localParticipant?.sid
    if (!userId) return

    const likeMessage = { userId, action: isLiked ? 'like' : 'unlike' }

    try {
      sendLike(new TextEncoder().encode(JSON.stringify(likeMessage)), { reliable: true })
    } catch (error) {
      console.error('Error sending like:', error)
    }
  }

  return { sendLikeToAll }
}

/**
 * A custom hook to receive 'like' messages in a LiveKit room.
 *
 * This hook provides an array of user IDs, `likedParticipants`, that have been liked by other participants in the room.
 * The hook automatically updates the `likedParticipants` array whenever a new 'like' message is received.
 *
 * @return {object} An object containing the `likedParticipants` array.
 */
export const useReceiveLike = () => {
  const { message } = useDataChannel('like')
  const { likedParticipants, setLikedParticipants } = useCustomLiveShoppingContext()

  useEffect(() => {
    if (message && message.payload) {
      try {
        if (!(message.payload instanceof Uint8Array)) return

        const decodedData = new TextDecoder().decode(message.payload)
        const likeMessage = JSON.parse(decodedData)

        if (!likeMessage.userId || !likeMessage.action) return

        setLikedParticipants((prevLikes) => {
          if (likeMessage.action === 'like' && !prevLikes.includes(likeMessage.userId)) {
            return [...prevLikes, likeMessage.userId]
          } else if (likeMessage.action === 'unlike') {
            return prevLikes.filter((id) => id !== likeMessage.userId)
          }
          return prevLikes
        })
      } catch (error) {
        console.error('Error decoding like message:', error)
      }
    }
  }, [message, setLikedParticipants])

  return { likedParticipants }
}

interface LikedParticipantsMessage {
  likedParticipants: string[]
}

/**
 * A custom hook to send a 'likedParticipantsList' message to a single participant in a LiveKit room.
 *
 * This hook provides a function, `sendLikedParticipants`, that takes a `RemoteParticipant` object as an argument.
 * The function sends a 'likedParticipantsList' message to the specified participant with the current list of
 * liked participants in the room.
 *
 * @return {object} An object containing the `sendLikedParticipants` function.
 */
export const useSendLikedParticipantsList = () => {
  const { send: sendLikedParticipantsList } = useDataChannel('likedParticipantsList')
  const room = useEnsureRoom()
  const { likedParticipants, setLikedParticipants } = useCustomRoomContext()

  const sendLikedParticipants = useCallback(
    (participant: RemoteParticipant) => {
      const currentLikedParticipants = [...likedParticipants]

      if (currentLikedParticipants.length === 0) {
        return
      }

      const message: LikedParticipantsMessage = {
        likedParticipants: currentLikedParticipants,
      }
      const options: DataPublishOptions = {
        reliable: true,
        destinationIdentities: [participant.identity],
      }
      const encodedMessage = new TextEncoder().encode(JSON.stringify(message))

      setTimeout(() => {
        sendLikedParticipantsList(encodedMessage, options)
      }, 3500)
    },
    [likedParticipants, sendLikedParticipantsList]
  )

  const handleParticipantDisconnected = useCallback(
    (disconnectedParticipant: RemoteParticipant) => {
      setLikedParticipants((prev) => prev.filter((participantSid) => participantSid !== disconnectedParticipant.sid))
    },
    [setLikedParticipants, likedParticipants]
  )

  useEffect(() => {
    if (!room) return

    room.addListener('participantConnected', (newParticipant) => {
      sendLikedParticipants(newParticipant)
    })

    room.addListener('participantDisconnected', handleParticipantDisconnected)

    return () => {
      room.removeListener('participantConnected', sendLikedParticipants)
      room.removeListener('participantDisconnected', handleParticipantDisconnected)
    }
  }, [room, sendLikedParticipants, handleParticipantDisconnected])
}

/**
 * A custom hook to receive 'likedParticipantsList' messages in a LiveKit room.
 *
 * This hook provides a function, `setLikedParticipants`, that updates the `likedParticipants` state in the
 * `CustomRoomContext` with the received list of liked participants.
 *
 * @return {object} An object containing the `setLikedParticipants` function.
 */
export const useReceiveLikedParticipantsList = () => {
  const { message } = useDataChannel('likedParticipantsList')
  const { setLikedParticipants } = useCustomRoomContext()

  useEffect(() => {
    if (!message) {
      return
    }

    const decodedMessage = new TextDecoder().decode(message.payload)
    const messageData = JSON.parse(decodedMessage)

    setLikedParticipants(messageData.likedParticipants)
  }, [message, setLikedParticipants])
}

/**
 * A custom hook to handle screen recording in a LiveKit room.
 *
 * This hook provides three functions: `startRecording`, `stopRecording`, and `recording`.
 * `startRecording` begins a screen recording, `stopRecording` stops the recording, and `recording`
 * is a boolean indicating whether a recording is currently in progress.
 *
 * If the `meetingEndTime` argument is provided, the recording will automatically stop when the
 * meeting is about to end.
 *
 * @param {string | undefined} meetingEndTime The end time of the meeting in ISO format.
 * @return {object} An object containing the `recording` boolean and `startRecording`, `stopRecording`
 *                  functions.
 */
export function useScreenRecording(meetingEndTime?: string) {
  const { recording, setRecording, endClicked, setEndClicked } = useCustomRoomContext()
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunksRef = useRef<Blob[]>([])
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const { name } = useRoomInfo()

  const getFormattedTimestamp = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = now.getHours()
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const hours12 = hours % 12 || 12
    return `${year}_${month}_${day}-${hours12}.${minutes}${ampm}`
  }

  const saveRecording = () => {
    if (recordedChunksRef.current.length === 0) return
    const timeString = getFormattedTimestamp()

    const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${name}-${timeString}.webm`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    recordedChunksRef.current = []
  }

  const startRecording = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' } as any,
        audio: { system: 'include', echoCancellation: true } as any,
      })

      const mediaRecorder = new MediaRecorder(mediaStream)

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        saveRecording()
        setRecording(false)
      }

      mediaStreamRef.current = mediaStream
      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start()
      setRecording(true)
    } catch (error) {
      console.error('Error starting screen recording:', error)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop())
      mediaStreamRef.current = null
    }

    setTimeout(() => {
      setRecording(false)
      saveRecording()
    }, 500)
  }

  useEffect(() => {
    if (!meetingEndTime || !recording) return

    const now = Date.now()
    const endTime = new Date(meetingEndTime).getTime()
    const timeUntilStop = endTime - now - 1500

    if (timeUntilStop <= 0) {
      stopRecording()
      return
    }

    const timer = setTimeout(() => {
      stopRecording()
    }, timeUntilStop)

    return () => clearTimeout(timer)
  }, [meetingEndTime, recording])

  useEffect(() => {
    if (endClicked && recording) {
      stopRecording()
      setEndClicked(false)
    }
  }, [endClicked, recording])

  return { recording, startRecording, stopRecording }
}

/**
 * Start/stop audio recording of the current meeting.
 *
 * Automatically stops recording when meetingEndTime is reached.
 * If no audio tracks are present, sets noTracks to true.
 * Returns the current recording state, function to start recording,
 * function to stop recording, and whether there are no audio tracks.
 *
 * @param meetingEndTime - time to automatically stop recording, in ISO string format
 * @returns {Object} - object with properties:
 *                    {boolean} audioRecording - whether audio recording is currently on
 *                    {function} startAudioRecording - starts audio recording
 *                    {function} stopAudioRecording - stops audio recording
 *                    {boolean} noTracks - whether there are no audio tracks present
 */
export function useAudioRecording(meetingEndTime?: string, setIsAudioRecording?: (value: boolean) => void) {
  const { audioRecording, setAudioRecording, endClicked, setEndClicked, noTracks, setNoTracks } = useCustomRoomContext()
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunksRef = useRef<Blob[]>([])
  const mixedStreamRef = useRef<MediaStream | null>(null)
  const { name } = useRoomInfo()
  const participants = useParticipants()

  const getFormattedTimestamp = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = now.getHours()
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const hours12 = hours % 12 || 12
    return `${year}_${month}_${day}-${hours12}.${minutes}${ampm}`
  }

  const saveAudioRecording = useCallback(async () => {
    if (recordedChunksRef.current.length === 0) {
      return
    }
    const timeString = getFormattedTimestamp()

    const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${name}-${timeString}.webm`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    recordedChunksRef.current = []
  }, [name])

  const startAudioRecording = async () => {
    try {
      const audioContext = new AudioContext()
      const destination = audioContext.createMediaStreamDestination()
      const participantAudioTracks: MediaStreamTrack[] = []

      participants.forEach((participant) => {
        participant.getTrackPublications().forEach((publication) => {
          if (publication.kind === Track.Kind.Audio && publication.track?.mediaStreamTrack) {
            participantAudioTracks.push(publication.track.mediaStreamTrack)
          }
        })
      })

      if (participantAudioTracks.length === 0) {
        setNoTracks(true)
        setIsAudioRecording?.(false)
        setAudioRecording(false)
        return
      }
      setNoTracks(false)

      participantAudioTracks.forEach((track) => {
        const stream = new MediaStream([track])
        const source = audioContext.createMediaStreamSource(stream)
        source.connect(destination)
      })

      const mixedStream = destination.stream
      if (mixedStream.getAudioTracks().length === 0) {
        setNoTracks(true)
        setIsAudioRecording?.(false)
        setAudioRecording(false)
        return
      }

      const mediaRecorder = new MediaRecorder(mixedStream, { mimeType: 'audio/webm' })

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        await saveAudioRecording()
        setIsAudioRecording?.(false)
        setAudioRecording(false)
      }

      mediaRecorder.onerror = (event) => {
        setIsAudioRecording?.(false)
        setAudioRecording(false)
      }

      mediaRecorderRef.current = mediaRecorder
      mixedStreamRef.current = mixedStream
      mediaRecorder.start()
      setIsAudioRecording?.(true)
      setAudioRecording(true)
    } catch (error) {
      setNoTracks(true)
      setIsAudioRecording?.(false)
      setAudioRecording(false)
    }
  }

  const stopAudioRecording = useCallback(async () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
    }

    if (mixedStreamRef.current) {
      mixedStreamRef.current.getTracks().forEach((track) => track.stop())
      mixedStreamRef.current = null
    }

    setTimeout(async () => {
      setAudioRecording(false)
      await saveAudioRecording()
    }, 500)
  }, [mediaRecorderRef, mixedStreamRef, setAudioRecording, saveAudioRecording])

  useEffect(() => {
    if (!meetingEndTime || !setIsAudioRecording) return

    const now = Date.now()
    const endTime = new Date(meetingEndTime).getTime()
    const timeUntilStop = endTime - now - 1500

    if (timeUntilStop <= 0) {
      stopAudioRecording()
      return
    }

    const timer = setTimeout(() => {
      stopAudioRecording()
    }, timeUntilStop)

    return () => clearTimeout(timer)
  }, [meetingEndTime, setIsAudioRecording, stopAudioRecording])

  useEffect(() => {
    if (endClicked && setIsAudioRecording) {
      stopAudioRecording()
      setEndClicked(false)
    }
  }, [endClicked, setIsAudioRecording, stopAudioRecording])

  return { audioRecording, startAudioRecording, stopAudioRecording, noTracks }
}

/**
 * A custom hook to send recording status to all participants in a room.
 *
 * @return {void}
 */
export const useSendRecordingStatus = () => {
  const { send: sendRecordingStatus } = useDataChannel('recordingStatus')
  const { recording } = useCustomRoomContext()
  const { localParticipant } = useLocalParticipant()

  useEffect(() => {
    const isHostOrCohost =
      JSON.parse(localParticipant?.metadata ?? '{}').userType === UserType.HOST ||
      JSON.parse(localParticipant?.metadata ?? '{}').userType === UserType.CO_HOST

    if (!isHostOrCohost) {
      return
    }

    const message = { recording }
    const encodedMessage = new TextEncoder().encode(JSON.stringify(message))

    const options: DataPublishOptions = {
      reliable: true,
    }

    sendRecordingStatus(encodedMessage, options)
  }, [recording, localParticipant?.metadata, sendRecordingStatus])
}

/**
 * A custom hook to receive and update the recording status in a LiveKit room.
 *
 * This hook listens to the 'recordingStatus' data channel and updates the
 * recording status state in the custom room context whenever a new message
 * is received.
 *
 * @return {void} This hook does not return any values.
 */

export const useReceiveRecordingStatus = () => {
  const { message } = useDataChannel('recordingStatus')
  const { setRecording } = useCustomRoomContext()

  useEffect(() => {
    if (!message) {
      return
    }

    const messageData = JSON.parse(new TextDecoder().decode(message.payload))
    setRecording(messageData.recording)
  }, [message, setRecording])
}

/**
 * A custom hook to send audio recording status to all participants in a room.
 *
 * This hook listens to the 'audioRecording' state in the custom room context
 * and sends a message to the 'audioRecordingStatus' data channel whenever the
 * state changes. The message contains the current value of `audioRecording`.
 *
 * Only hosts and co-hosts can send the audio recording status.
 *
 * @return {void} This hook does not return any values.
 */
export const useSendAudioRecordingStatus = () => {
  const { send: sendAudioRecordingStatus } = useDataChannel('audioRecordingStatus')
  const { audioRecording } = useCustomRoomContext()
  const { localParticipant } = useLocalParticipant()

  useEffect(() => {
    const userMeta = JSON.parse(localParticipant?.metadata ?? '{}')
    const isHostOrCohost = userMeta.userType === UserType.HOST || userMeta.userType === UserType.CO_HOST

    if (!isHostOrCohost) return

    const message = { audioRecording }
    const encodedMessage = new TextEncoder().encode(JSON.stringify(message))

    const options: DataPublishOptions = {
      reliable: true,
    }

    sendAudioRecordingStatus(encodedMessage, options)
  }, [audioRecording, localParticipant?.metadata, sendAudioRecordingStatus])
}

/**
 * A custom hook to receive and update the audio recording status in a LiveKit room.
 *
 * This hook listens to the 'audioRecordingStatus' data channel and updates the
 * `audioRecording` state in the custom room context whenever a new message
 * is received.
 *
 * @return {void} This hook does not return any values.
 */
export const useReceiveAudioRecordingStatus = () => {
  const { message } = useDataChannel('audioRecordingStatus')
  const { setAudioRecording } = useCustomRoomContext()

  useEffect(() => {
    if (!message) return

    const messageData = JSON.parse(new TextDecoder().decode(message.payload))
    setAudioRecording(messageData.audioRecording)
  }, [message, setAudioRecording])
}
